// ── PARENT: MERCH STORE (self-serve, seasonal apparel sales) ─────────
// esc stays a bare identifier — it's a top-level `const` in /js/shared/esc.js's
// classic script, part of the shared global lexical environment every script
// on the page can already see.
// cart: flat array of lines, not keyed by performer — a line's recipient is
// either one of the family's performers or a free-text name (self, sibling,
// grandparent), and a family may check out multiple times within one sale
// (e.g. the shirt now, tights later once measured), so state doesn't fit a
// per-performer shape.
//
// Buttons wired via VIEW_INIT['p-store'] + wireParentStoreUnitRows() (for the
// "Add an item" size/for rows, which siRenderUnitRows() also rewrites
// directly, bypassing VIEW_INIT — same wireXUnitRows shape as admin-store.js's
// preview cart). pShopLoad() rewrites #main directly too and re-wires
// afterward for the same reason.
import { DB, session, STORE_SALES, currentView } from '../core/state.js';
import { apiFetch } from '../core/api.js';
import { fmtLocalDT } from '../core/format.js';
import { openModal, closeModal, toast, toastError } from '../core/ui.js';
import { VIEWS, VIEW_INIT, refresh } from '../core/routing.js';
import { showChargeError } from './billing-shared.js';
import { REQUIRED_CATEGORY_LABELS } from './admin-store.js';

let storeCart = [];
let pShopSaleId = null;
let pShopOrders = null;    // null = not loaded yet for pShopSaleId
let pShopRequired = null;  // { categories, performers } — required-item status for this family
let pShopLoadingKey = null;
let pShopAttestBusy = {};
// One entry per unit being added in the "Add an item" picker — quantity > 1
// doesn't just multiply a single size/recipient, it grows this into that many
// independently-sized, independently-addressed rows (e.g. two shirts, two
// different kids, two different sizes, added in one go). Reset to a single
// default row after every successful add.
let siUnits = [];

async function pShopLoad(saleId) {
  pShopSaleId = saleId;
  pShopLoadingKey = saleId;
  if (currentView === 'p-store') { document.getElementById('main').innerHTML = VIEWS['p-store'](); wireParentStore(); }
  try {
    const [orders, required] = await Promise.all([
      apiFetch('/store/orders?saleId=' + saleId),
      apiFetch('/store/sales/' + saleId + '/my-required-status'),
    ]);
    if (pShopLoadingKey !== saleId) return;
    pShopOrders = orders;
    pShopRequired = required;
  } catch (e) {
    if (pShopLoadingKey !== saleId) return;
    pShopOrders = [];
    pShopRequired = { categories: [], performers: [] };
    toastError(e.message);
  }
  pShopLoadingKey = null;
  if (currentView === 'p-store') { document.getElementById('main').innerHTML = VIEWS['p-store'](); wireParentStore(); }
}
function pShopSelectSale(saleId) {
  storeCart = [];
  pShopOrders = null;
  pShopRequired = null;
  pShopLoad(saleId);
}
function siCurrentProduct() {
  const sale = STORE_SALES.find(s => s.id === pShopSaleId);
  const productId = document.getElementById('siProduct') ? document.getElementById('siProduct').value : null;
  return sale && sale.products.find(p => p.id === productId);
}
// Renders the per-unit Size/For rows — pure function of siUnits + the given
// product (not read from the DOM), so it works both for the initial page
// render (before #siProduct exists) and for live re-renders after an edit.
function siUnitRowsHtml(product) {
  const sizes = (product && product.sizes) || [];
  const kids = DB.children.filter(c => c.accountId === session.id);
  return siUnits.map((u, i) => `
    <div style="padding:10px;border:1px solid var(--border-soft);border-radius:8px;margin-bottom:8px">
      ${siUnits.length > 1 ? `<div style="font-size:11px;text-transform:uppercase;letter-spacing:0.08em;color:var(--muted);margin-bottom:8px">Item ${i + 1}</div>` : ''}
      <div class="field-row">
        <div class="field"><label>Size</label>
          <select data-action="si-unit-field" data-index="${i}" data-field="size">
            ${sizes.map(s => `<option value="${esc(s)}" ${u.size === s ? 'selected' : ''}>${esc(s)}</option>`).join('')}
          </select>
        </div>
        <div class="field"><label>For</label>
          <select data-action="si-unit-field" data-index="${i}" data-field="performerId">
            ${kids.map(k => `<option value="${k.id}" ${u.performerId === k.id ? 'selected' : ''}>${esc(k.name)}</option>`).join('')}
            <option value="" ${!u.performerId ? 'selected' : ''}>Someone else</option>
          </select>
        </div>
      </div>
      ${!u.performerId ? `<div class="field"><label>Name</label><input type="text" value="${esc(u.recipientName || '')}" placeholder="e.g. Grandma" data-action="si-unit-field" data-index="${i}" data-field="recipientName"></div>` : ''}
    </div>`).join('');
}
// Called after the initial full render (via wireParentStore) and after every
// direct rewrite of #siUnitRows below (siProductChanged, siQtyChanged,
// siUnitFieldChanged) — those bypass VIEW_INIT, so the rows they just wrote
// need their own re-wire.
function wireParentStoreUnitRows() {
  const container = document.getElementById('siUnitRows');
  if (!container) return;
  container.querySelectorAll('[data-action="si-unit-field"]').forEach(el => {
    el.addEventListener('change', () => siUnitFieldChanged(parseInt(el.dataset.index), el.dataset.field, el.value));
  });
}
function siRenderUnitRows() {
  const container = document.getElementById('siUnitRows');
  if (container) container.innerHTML = siUnitRowsHtml(siCurrentProduct());
  wireParentStoreUnitRows();
}
function siProductChanged() {
  const product = siCurrentProduct();
  const kids = DB.children.filter(c => c.accountId === session.id);
  const qtyInput = document.getElementById('siQty');
  if (qtyInput) qtyInput.value = 1;
  siUnits = [{ size: (product && product.sizes[0]) || '', performerId: (kids[0] && kids[0].id) || '', recipientName: '' }];
  siRenderUnitRows();
  const photo = document.getElementById('siPhoto');
  if (photo) {
    if (product && product.photoUrl) { photo.src = product.photoUrl; photo.style.display = ''; }
    else { photo.style.display = 'none'; }
  }
}
function siPhotoZoom() {
  const product = siCurrentProduct();
  if (!product || !product.photoUrl) return;
  openModal(`<img src="${esc(product.photoUrl)}" style="width:100%;border-radius:8px;display:block">
    <div style="text-align:center;font-size:13px;color:var(--muted);margin-top:10px">${esc(product.name)}</div>`);
}
// Mirrors StoreRoutes::MAX_LINE_QUANTITY. Clamped here as well as rejected on
// the server, so a family sees the limit as they type rather than losing a
// filled-in cart to a 400 at checkout.
const MAX_LINE_QUANTITY = 20;

function siQtyChanged() {
  const qtyInput = document.getElementById('siQty');
  const qty = Math.min(MAX_LINE_QUANTITY, Math.max(1, parseInt(qtyInput.value) || 1));
  qtyInput.value = qty;
  const product = siCurrentProduct();
  const kids = DB.children.filter(c => c.accountId === session.id);
  const defaultSize = (product && product.sizes[0]) || '';
  const defaultPerformer = (kids[0] && kids[0].id) || '';
  while (siUnits.length < qty) siUnits.push({ size: defaultSize, performerId: defaultPerformer, recipientName: '' });
  while (siUnits.length > qty) siUnits.pop();
  siRenderUnitRows();
}
function siUnitFieldChanged(index, field, value) {
  if (!siUnits[index]) return;
  siUnits[index][field] = value;
  if (field === 'performerId' && value) siUnits[index].recipientName = '';
  siRenderUnitRows();
}
// Jumps the "Add an item" picker to the first active product for a required
// category, so the "Order it" shortcut on the checklist doesn't require the
// parent to hunt for the right item in the dropdown themselves.
function storeQuickAddCategory(category) {
  const sale = STORE_SALES.find(s => s.id === pShopSaleId);
  const product = sale && sale.products.find(p => p.requiredCategory === category && p.active !== false);
  const sel = document.getElementById('siProduct');
  if (!product || !sel) return;
  sel.value = product.id;
  siProductChanged();
  sel.scrollIntoView({ behavior: 'smooth', block: 'center' });
}
function storeCartAddFromForm() {
  const productId = document.getElementById('siProduct').value;
  if (!productId) { toast('Choose an item'); return; }
  if (!siUnits.length) { toast('Add at least one item'); return; }
  for (const u of siUnits) {
    if (!u.size) { toast('Choose a size for every item'); return; }
    if (!u.performerId && !u.recipientName.trim()) { toast('Enter who each item is for'); return; }
  }
  siUnits.forEach(u => {
    storeCart.push({ productId, size: u.size, quantity: 1, performerId: u.performerId || null, recipientName: u.performerId ? null : u.recipientName.trim() });
  });
  siUnits = []; // next render re-seeds a single fresh row
  refresh();
  toast('Added to cart');
}
function storeCartRemove(index) { storeCart.splice(index, 1); refresh(); }
function storeCartTotal() {
  const sale = STORE_SALES.find(s => s.id === pShopSaleId);
  if (!sale) return 0;
  return storeCart.reduce((sum, line) => {
    const product = sale.products.find(p => p.id === line.productId);
    return sum + (product ? product.price * line.quantity : 0);
  }, 0);
}
async function storeAttest(saleId, performerId, category) {
  const key = performerId + ':' + category;
  if (pShopAttestBusy[key]) return;
  pShopAttestBusy[key] = true;
  try {
    await apiFetch('/store/attestations', { method: 'POST', body: JSON.stringify({ saleId, performerId, category }) });
    toast('Marked as already owned');
    await pShopLoad(saleId);
  } catch (e) { toastError(e.message); }
  pShopAttestBusy[key] = false;
}
async function storeUnattest(attestationId, saleId) {
  try {
    await apiFetch('/store/attestations/' + attestationId, { method: 'DELETE' });
    toast('Undone');
    await pShopLoad(saleId);
  } catch (e) { toastError(e.message); }
}
function storeCheckoutConfirm() {
  if (!storeCart.length) return;
  const total = storeCartTotal();
  const card = session.card ? ` to your ${esc(session.card.brand)} ending in ${esc(session.card.last4)}` : '';
  openModal(`
    <h3>Confirm order</h3>
    <p class="card-intro">Place this order and charge <b style="color:var(--cream)">${fmt$(total)}</b>${card}?</p>
    <div style="display:flex;gap:10px">
      <button class="btn" style="flex:1" id="storePayBtn">Confirm — charge ${fmt$(total)}</button>
      <button class="btn-ghost" style="flex:1" onclick="closeModal()">Cancel</button>
    </div>`, true);
  document.getElementById('storePayBtn').addEventListener('click', processStoreCheckout);
}
async function processStoreCheckout() {
  const btn = document.getElementById('storePayBtn');
  if (btn) { btn.disabled = true; btn.style.opacity = '0.7'; btn.style.cursor = 'wait'; btn.textContent = 'Processing…'; }
  try {
    await apiFetch('/store/orders', {
      method: 'POST',
      body: JSON.stringify({
        saleId: pShopSaleId,
        items: storeCart.map(l => ({ productId: l.productId, size: l.size, quantity: l.quantity, performerId: l.performerId, recipientName: l.recipientName })),
      })
    });
    storeCart = [];
    DB.charges = await apiFetch('/charges?accountId=' + session.id);
    await pShopLoad(pShopSaleId);
    closeModal(); refresh();
    toast('Order placed! A confirmation email is on its way.');
  } catch (e) {
    if (btn) { btn.disabled = false; btn.style.opacity = '1'; btn.style.cursor = 'pointer'; btn.textContent = 'Confirm'; }
    showChargeError(e.message || 'The charge was declined. Your order was not placed.');
  }
}
VIEWS['p-store'] = () => {
  const sales = STORE_SALES.filter(s => s.active !== false && s.isOpen !== false);
  if (!sales.length) {
    return `<div class="page-head"><h2 class="serif">Merch <em>Store</em></h2></div>
      <div class="card"><p class="empty">No merch sales are open right now — check back soon.</p></div>`;
  }
  if (!pShopSaleId || !sales.some(s => s.id === pShopSaleId)) pShopSaleId = sales[0].id;
  if (pShopOrders === null && pShopLoadingKey !== pShopSaleId) pShopLoad(pShopSaleId);

  const sale = sales.find(s => s.id === pShopSaleId);
  const kids = DB.children.filter(c => c.accountId === session.id);
  const card = session.card ? `${esc(session.card.brand)} ending in ${esc(session.card.last4)}` : null;
  const total = storeCartTotal();
  const activeProducts = sale.products.filter(p => p.active !== false);

  const saleTabs = sales.length > 1 ? `
    <div style="display:flex;flex-wrap:wrap;gap:8px;margin-bottom:18px">
      ${sales.map(s => `<button class="btn-ghost btn-sm" style="${s.id === pShopSaleId ? 'border-color:rgba(201,168,76,0.5);color:var(--gold-light)' : ''}" data-action="pshop-select-sale" data-sale="${s.id}">${esc(s.name)}</button>`).join('')}
    </div>` : '';

  const requiredBlock = (pShopRequired && pShopRequired.categories.length && pShopRequired.performers.length) ? `
    <div class="card" style="margin-bottom:16px">
      <div class="card-hd">Still needed for the show</div>
      ${pShopRequired.performers.map(p => `
        <div style="padding:10px 0;border-bottom:1px solid var(--border-soft)">
          <div style="font-size:13px;color:var(--cream);margin-bottom:8px">${esc(p.performerName)}</div>
          <div style="display:flex;flex-direction:column;gap:8px">
            ${pShopRequired.categories.map(cat => {
              const st = p.statuses[cat];
              const label = REQUIRED_CATEGORY_LABELS[cat] || cat;
              if (st.status === 'ordered') {
                return `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap">
                  <span style="font-size:13px;color:var(--cream)">${esc(label)}</span>
                  <span class="pill pill-paid">Ordered</span>
                </div>`;
              }
              if (st.status === 'attested') {
                return `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap">
                  <span style="font-size:13px;color:var(--cream)">${esc(label)}</span>
                  <span style="display:flex;align-items:center;gap:8px">
                    <span class="pill pill-amber">Already owns</span>
                    <button class="btn-ghost btn-sm" data-action="store-unattest" data-attestation="${st.attestationId}" data-sale="${sale.id}">Undo</button>
                  </span>
                </div>`;
              }
              return `<div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap">
                <span style="font-size:13px;color:var(--cream)">${esc(label)}</span>
                <span style="display:flex;align-items:center;gap:8px;flex-wrap:wrap">
                  <span class="pill pill-due">Needed</span>
                  <button class="btn-ghost btn-sm" data-action="store-quick-add" data-category="${cat}">Order it</button>
                  <button class="btn-ghost btn-sm" data-action="store-attest" data-sale="${sale.id}" data-performer="${p.performerId}" data-category="${cat}">Already have one</button>
                </span>
              </div>`;
            }).join('')}
          </div>
        </div>`).join('')}
    </div>` : '';

  const ordersBlock = (pShopOrders && pShopOrders.length) ? `
    <div class="card" style="margin-bottom:16px">
      <div class="card-hd">Your orders so far</div>
      ${pShopOrders.map(o => `
        <div style="padding:8px 0;border-bottom:1px solid var(--border-soft)">
          <div style="display:flex;justify-content:space-between;font-size:12px;color:var(--muted)">
            <span>${fmtLocalDT(o.createdAt)}</span><span>${fmt$(o.totalAmount)}</span>
          </div>
          ${o.items.map(i => `<div style="font-size:13px;color:var(--cream)">${esc(i.productName)} (${esc(i.size)}) x${i.quantity} — ${esc(i.performerId ? ((kids.find(k => k.id === i.performerId) || {}).name || 'Performer') : i.recipientName)}</div>`).join('')}
        </div>`).join('')}
    </div>` : '';

  if (!siUnits.length && activeProducts.length) {
    siUnits = [{ size: activeProducts[0].sizes[0] || '', performerId: (kids[0] && kids[0].id) || '', recipientName: '' }];
  }
  const addItemPanel = activeProducts.length ? `
    <div class="card">
      <div class="card-hd">Add an item</div>
      <div class="field"><label>Item</label>
        <select id="siProduct" data-action="si-product-changed">
          ${activeProducts.map(p => `<option value="${p.id}">${esc(p.name)} — ${fmt$(p.price)}</option>`).join('')}
        </select>
      </div>
      <img id="siPhoto" src="${esc(activeProducts[0].photoUrl || '')}" style="display:${activeProducts[0].photoUrl ? '' : 'none'};max-width:100%;max-height:220px;border-radius:8px;margin-bottom:14px;object-fit:contain;background:rgba(255,255,255,0.03);cursor:zoom-in" onerror="this.style.display='none'" data-action="si-photo-zoom" title="Click to enlarge">
      <div class="field"><label>Quantity</label><input type="number" id="siQty" min="1" max="20" value="${siUnits.length}" data-action="si-qty-changed"></div>
      <div id="siUnitRows">${siUnitRowsHtml(activeProducts[0])}</div>
      <button class="btn" data-action="store-cart-add">Add to cart</button>
    </div>` : `<div class="card"><p class="empty">No items available in this sale right now.</p></div>`;

  const cartSummary = `
    <div class="card" style="position:sticky;top:24px">
      <div class="card-hd">Your cart</div>
      ${storeCart.length ? `<div style="display:flex;flex-direction:column;gap:10px;margin-bottom:16px">
        ${storeCart.map((line, i) => {
          const product = sale.products.find(p => p.id === line.productId);
          const recipientLabel = line.performerId ? ((kids.find(k => k.id === line.performerId) || {}).name || 'Performer') : line.recipientName;
          return `<div style="display:flex;justify-content:space-between;gap:10px;font-size:13px">
            <div>
              <div style="color:var(--cream)">${esc(product ? product.name : '—')} (${esc(line.size)}) x${line.quantity}</div>
              <div style="font-size:11px;color:var(--muted)">${esc(recipientLabel)}</div>
            </div>
            <div style="display:flex;align-items:center;gap:8px;flex-shrink:0">
              <span style="color:var(--gold-light)">${fmt$(product ? product.price * line.quantity : 0)}</span>
              <button class="icon-btn" title="Remove" aria-label="Remove" data-action="store-cart-remove" data-index="${i}">&times;</button>
            </div>
          </div>`;
        }).join('')}
      </div>
      <hr style="border:none;border-top:1px solid var(--border-soft);margin:0 0 14px">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
        <span style="font-size:14px;color:var(--cream)">Total due today</span>
        <span style="font-family:'Libre Baskerville',serif;font-size:26px;font-weight:700;color:var(--cream)">${fmt$(total)}</span>
      </div>
      ${card
        ? `<div style="font-size:12px;color:var(--muted);margin-bottom:14px">Charged to your card on file (${card})</div>
           <button class="btn" style="width:100%" data-action="store-checkout-confirm">Place order — charge ${fmt$(total)}</button>`
        : `<div style="font-size:12px;color:var(--red);margin-bottom:14px">No card on file. Add one in Billing first.</div>
           <button class="btn" style="width:100%" disabled>Place order</button>`}`
      : `<p class="summary-empty" style="text-align:center;padding:12px 0;font-style:italic;color:var(--muted)">Add items to get started.</p>`}
    </div>`;

  return `
    <div class="page-head">
      <h2 class="serif">Merch <em>Store</em></h2>
      <p class="sub">${esc(sale.name)} — orders close ${fmtLocalDT(sale.deadlineAt)}. Charged once to your card on file, separate from tuition.</p>
    </div>
    ${saleTabs}
    ${requiredBlock}
    ${ordersBlock}
    <div class="grid-2" style="align-items:start">
      <div>${addItemPanel}</div>
      <div>${cartSummary}</div>
    </div>`;
};
function wireParentStore() {
  document.querySelectorAll('[data-action="pshop-select-sale"]').forEach(btn => {
    btn.addEventListener('click', () => pShopSelectSale(btn.dataset.sale));
  });
  document.querySelectorAll('[data-action="store-unattest"]').forEach(btn => {
    btn.addEventListener('click', () => storeUnattest(btn.dataset.attestation, btn.dataset.sale));
  });
  document.querySelectorAll('[data-action="store-quick-add"]').forEach(btn => {
    btn.addEventListener('click', () => storeQuickAddCategory(btn.dataset.category));
  });
  document.querySelectorAll('[data-action="store-attest"]').forEach(btn => {
    btn.addEventListener('click', () => storeAttest(btn.dataset.sale, btn.dataset.performer, btn.dataset.category));
  });
  const productEl = document.getElementById('siProduct');
  if (productEl) productEl.addEventListener('change', siProductChanged);
  const photoEl = document.getElementById('siPhoto');
  if (photoEl) photoEl.addEventListener('click', siPhotoZoom);
  const qtyEl = document.getElementById('siQty');
  if (qtyEl) qtyEl.addEventListener('change', siQtyChanged);
  wireParentStoreUnitRows();
  document.querySelectorAll('[data-action="store-cart-add"]').forEach(btn => {
    btn.addEventListener('click', storeCartAddFromForm);
  });
  document.querySelectorAll('[data-action="store-cart-remove"]').forEach(btn => {
    btn.addEventListener('click', () => storeCartRemove(parseInt(btn.dataset.index)));
  });
  document.querySelectorAll('[data-action="store-checkout-confirm"]').forEach(btn => {
    btn.addEventListener('click', storeCheckoutConfirm);
  });
}
VIEW_INIT['p-store'] = wireParentStore;
